// ==========================================================================
// screens/MoneyImport.tsx — load a bank statement CSV (FF-010, 9 Oct 2026)
//   1. Pick the account and drop the file (nothing is uploaded yet).
//   2. Check the columns. The app guesses; fix anything wrong once and it is
//      remembered for that bank next time (import_profiles).
//   3. Import. Rows already in the app are skipped, so overlapping exports
//      are safe. The same file twice is refused.
// ==========================================================================
import { useMemo, useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { CheckCircle2, FileUp, AlertTriangle } from "lucide-react";
import { Button, Card, ErrorText } from "@/components/ui";
import { inputCls } from "@/components/Sheet";
import { ACCOUNT_KINDS, money, useAccounts } from "@/lib/money";
import { DATE_FORMATS, applyMapping, guessMapping, parseCsv, type Mapping } from "@/lib/csv";
import { useImport, useProfiles, type ImportResult } from "@/lib/transactions";

export default function MoneyImport() {
  const accounts = useAccounts(); const profiles = useProfiles(); const imp = useImport();
  const open = (accounts.data ?? []).filter((a) => !a.closed_on && a.kind !== "broker");
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [profileName, setProfileName] = useState("");
  const [drag, setDrag] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const account = open.find((a) => a.id === accountId);
  const rows = useMemo(() => (file ? parseCsv(file.text) : []), [file]);
  const parsed = useMemo(() => (mapping ? applyMapping(rows, mapping) : null), [rows, mapping]);
  const head = mapping?.hasHeader ? rows[0] : rows[0]?.map((_, i) => `Column ${i + 1}`);
  const cols = (head ?? []).map((h, i) => ({ i, label: h || `Column ${i + 1}` }));

  async function load(f: File) {
    const text = await f.text();
    setFile({ name: f.name, text }); setResult(null); imp.reset();
    const name = `${account?.institution ?? "Bank"} CSV`;
    const saved = profiles.data?.find((p) => p.name === name);
    setProfileName(name);
    setMapping(saved ? saved.mapping : guessMapping(parseCsv(text)));
  }
  function onDrop(e: DragEvent) { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) void load(f); }
  const set = <K extends keyof Mapping>(k: K, v: Mapping[K]) => setMapping((m) => (m ? { ...m, [k]: v } : m));

  const colSelect = (label: string, k: "date" | "description" | "amount" | "debit" | "credit" | "balance", optional = false) => (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-ink-muted">{label}</span>
      <select value={mapping?.[k] ?? ""} onChange={(e) => set(k, e.target.value === "" ? (null as never) : (Number(e.target.value) as never))} className={inputCls}>
        {optional && <option value="">Not in this file</option>}
        {cols.map((c) => <option key={c.i} value={c.i}>{c.label}</option>)}
      </select>
    </label>
  );

  if (result) {
    return (
      <Card className="mx-auto max-w-xl space-y-4 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-good" />
        <h2 className="font-heading text-2xl font-semibold">Imported</h2>
        <p><span className="num text-3xl font-semibold text-primary">{result.added}</span> new transactions into {account?.name}</p>
        <p className="text-sm text-ink-muted">{result.skipped > 0 ? `${result.skipped} were already in the app and were skipped. ` : ""}{result.from && `${format(parseISO(result.from), "d MMM")} – ${format(parseISO(result.to), "d MMM yyyy")}`}</p>
        <div className="flex justify-center gap-3">
          <Link to="/money/transactions" className="rounded-full bg-primary px-5 py-2.5 font-semibold text-white">Code them</Link>
          <button type="button" onClick={() => { setFile(null); setMapping(null); setResult(null); }} className="rounded-full px-5 py-2.5 font-semibold ring-1 ring-line">Import another</button>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
      <div className="space-y-5">
        <Card className="space-y-4">
          <h2 className="font-semibold">1. Which account?</h2>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputCls}>
            <option value="">Choose an account</option>
            {open.map((a) => <option key={a.id} value={a.id}>{a.name}{a.institution ? ` · ${a.institution}` : ""} ({ACCOUNT_KINDS.find((k) => k.key === a.kind)?.label})</option>)}
          </select>
          <p className="text-xs text-ink-muted">Not listed? Add it under Settings → Accounts first.</p>
        </Card>
        <Card className="space-y-3">
          <h2 className="font-semibold">2. The CSV file</h2>
          <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
            className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition ${drag ? "border-primary bg-primary/5" : "border-line hover:border-primary/50"} ${!accountId ? "pointer-events-none opacity-40" : ""}`}>
            <FileUp className="h-8 w-8 text-primary" />
            <span className="font-medium">{file ? file.name : "Drop the CSV here, or click to choose"}</span>
            <span className="text-xs text-ink-muted">Download it from internet banking as CSV. It stays on this computer until you press Import.</span>
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void load(f); }} />
          </label>
        </Card>
        {mapping && (
          <Card className="space-y-3">
            <h2 className="font-semibold">3. Check the columns</h2>
            <label className="flex items-center justify-between text-sm"><span>First row is headings</span><input type="checkbox" checked={mapping.hasHeader} onChange={(e) => set("hasHeader", e.target.checked)} className="h-5 w-5 accent-primary" /></label>
            {colSelect("Date", "date")}
            <label className="block space-y-1"><span className="text-sm font-medium text-ink-muted">Date looks like</span>
              <select value={mapping.dateFormat} onChange={(e) => set("dateFormat", e.target.value as Mapping["dateFormat"])} className={inputCls}>{DATE_FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label} ({f.example})</option>)}</select></label>
            {colSelect("Description", "description")}
            {colSelect("Amount (money in +, out −)", "amount", true)}
            {mapping.amount === null && <div className="grid grid-cols-2 gap-3">{colSelect("Money out", "debit", true)}{colSelect("Money in", "credit", true)}</div>}
            {colSelect("Balance", "balance", true)}
            <label className="flex items-center justify-between text-sm"><span>Flip signs (if spending shows as positive)</span><input type="checkbox" checked={mapping.flipSign} onChange={(e) => set("flipSign", e.target.checked)} className="h-5 w-5 accent-primary" /></label>
            <label className="block space-y-1"><span className="text-sm font-medium text-ink-muted">Remember these settings as</span><input value={profileName} onChange={(e) => setProfileName(e.target.value)} className={inputCls} /></label>
          </Card>
        )}
      </div>

      <div className="space-y-4">
        {!parsed ? (
          <Card className="flex min-h-[320px] flex-col items-center justify-center text-center text-ink-muted">
            <FileUp className="h-10 w-10" />
            <p className="mt-3 max-w-sm">Pick an account and a CSV. A preview of what will be imported shows here before anything is saved.</p>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4">
              <Card><div className="num text-2xl font-semibold">{parsed.rows.length}</div><div className="text-xs text-ink-muted">rows read</div></Card>
              <Card><div className="num text-2xl font-semibold text-good">{money(parsed.rows.filter((r) => r.amount_cents > 0).reduce((s, r) => s + r.amount_cents, 0))}</div><div className="text-xs text-ink-muted">money in</div></Card>
              <Card><div className="num text-2xl font-semibold">{money(-parsed.rows.filter((r) => r.amount_cents < 0).reduce((s, r) => s + r.amount_cents, 0))}</div><div className="text-xs text-ink-muted">money out</div></Card>
            </div>
            {parsed.bad.length > 0 && (
              <div className="flex gap-3 rounded-lg bg-warn/10 p-4 text-sm text-warn">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <div>{parsed.bad.length} row{parsed.bad.length === 1 ? "" : "s"} couldn't be read and will be left out: {parsed.bad.slice(0, 3).map((b) => `line ${b.line} (${b.reason})`).join(", ")}{parsed.bad.length > 3 ? "…" : ""}. Check the column choices.</div>
              </div>
            )}
            <Card className="overflow-hidden p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-line bg-surface-raised/60 text-left text-xs text-ink-muted"><tr><th className="px-4 py-2 font-medium">Date</th><th className="px-2 py-2 font-medium">Description</th><th className="px-4 py-2 text-right font-medium">Amount</th></tr></thead>
                <tbody>
                  {parsed.rows.slice(0, 12).map((r, i) => (
                    <tr key={i} className="border-t border-line/60">
                      <td className="num whitespace-nowrap px-4 py-2 text-ink-muted">{format(parseISO(r.posted_on), "d MMM yyyy")}</td>
                      <td className="max-w-0 truncate px-2 py-2">{r.description}</td>
                      <td className={`num whitespace-nowrap px-4 py-2 text-right font-medium ${r.amount_cents > 0 ? "text-good" : ""}`}>{money(r.amount_cents, { cents: true })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsed.rows.length > 12 && <p className="border-t border-line px-4 py-2 text-xs text-ink-muted">and {parsed.rows.length - 12} more</p>}
            </Card>
            <ErrorText>{imp.error ? (imp.error as Error).message : null}</ErrorText>
            <Button disabled={!accountId || parsed.rows.length === 0 || imp.isPending} onClick={async () => {
              const r = await imp.mutateAsync({ accountId, fileName: file!.name, fileText: file!.text, rows: parsed.rows, mapping: mapping!, profileName: profileName.trim() || "Bank CSV" });
              setResult(r);
            }} className="lg:!w-auto lg:px-8">{imp.isPending ? "Importing…" : `Import ${parsed.rows.length} rows into ${account?.name ?? "the account"}`}</Button>
          </>
        )}
      </div>
    </div>
  );
}
